/**
 * ListPageTemplate — Matches OnMove-Admin ListTemplate layout exactly
 *
 * Layout:
 *  1. Glassmorphism Card wrapper
 *  2. Gradient header bar (floating mt={-3}) with title + column visibility icon
 *  3. Full-width search field below header (in card body)
 *  4. KDTable with sorting, pagination, entries-per-page
 *
 * Column visibility:
 *  - `columns` = all available columns
 *  - `defaultVisibleFields` = which fields are shown by default
 *  - ViewColumnIcon in header opens checkbox menu to toggle columns
 */

import PropTypes from "prop-types";
import { useState, useMemo, useCallback } from "react";
import {
  Card,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";

import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDTable from "components/KDTable";
import KDSnackbar from "components/KDSnackbar";
import DashboardNavbar from "components/DashboardNavbar";
import { useMaterialUIController } from "context";

function ListPageTemplate({
  title,
  columns,
  rows,
  loading,
  error,
  emptyMessage,
  onRowClick,
  searchable,
  searchPlaceholder,
  defaultSortField,
  defaultSortDirection,
  headerColor,
  defaultVisibleFields,
  extraToolbarActions,
  snackbar,
  onSnackbarClose,
}) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, darkMode } = controller;
  const { palette } = useTheme();

  // --- Search ---
  const [searchValue, setSearchValue] = useState("");

  // --- Column visibility ---
  const allFieldNames = useMemo(() => columns.map((c) => c.field), [columns]);
  const [visibleFields, setVisibleFields] = useState(
    () => defaultVisibleFields || allFieldNames
  );
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);

  const handleToggleColumn = useCallback((fieldName) => {
    setVisibleFields((prev) =>
      prev.includes(fieldName)
        ? prev.filter((f) => f !== fieldName)
        : [...prev, fieldName]
    );
  }, []);

  // --- Filter columns ---
  const visibleColumns = useMemo(
    () => columns.filter((col) => visibleFields.includes(col.field)),
    [columns, visibleFields]
  );

  // --- Search filter ---
  const filteredRows = useMemo(() => {
    if (!searchable || !searchValue.trim()) return rows;
    const q = searchValue.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((val) =>
        val != null && String(val).toLowerCase().includes(q)
      )
    );
  }, [rows, searchValue, searchable]);

  return (
    <KDBox
      sx={({ breakpoints, transitions }) => ({
        p: 3,
        marginLeft: miniSidenav ? "80px" : "260px",
        transition: transitions.create("margin-left", {
          easing: transitions.easing.easeInOut,
          duration: transitions.duration.standard,
        }),
        [breakpoints.down("xl")]: { marginLeft: 0 },
      })}
    >
      <DashboardNavbar />

      {/* Card wrapper — glassmorphism */}
      <Card
        sx={{
          mt: 5,
          overflow: "visible",
          background: palette.background.glass,
          backdropFilter: "blur(10px)",
          border: `1px solid ${palette.background.glassBorder}`,
          borderRadius: "20px",
        }}
      >
        {/* ── Gradient header bar ── */}
        <KDBox
          mx={2}
          mt={-3}
          py={2.5}
          px={3}
          sx={({ palette }) => {
            const grad = palette.gradients?.[headerColor] || palette.gradients?.info;
            const mainColor = palette[headerColor]?.main || palette.info.main;
            return {
              background: grad
                ? `linear-gradient(135deg, ${grad.state} 0%, ${grad.main} 100%)`
                : `linear-gradient(135deg, ${mainColor}, ${mainColor})`,
              borderRadius: "16px",
              boxShadow: `0 8px 32px ${mainColor}40`,
            };
          }}
        >
          <KDBox display="flex" justifyContent="space-between" alignItems="center">
            <KDTypography variant="h6" fontWeight="bold" color="white" sx={{ letterSpacing: "0.5px" }}>
              {title}
            </KDTypography>

            {/* Right side: extra actions + column visibility icon */}
            <KDBox display="flex" alignItems="center" gap={1}>
              {extraToolbarActions}

              {/* Column visibility toggle */}
              <IconButton
                onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
                sx={{
                  color: "white",
                  backgroundColor: palette.headerOverlay.icon,
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: palette.headerOverlay.iconHover },
                }}
              >
                <ViewColumnIcon />
              </IconButton>
            </KDBox>
          </KDBox>
        </KDBox>

        {/* ── Column visibility menu ── */}
        <Menu
          anchorEl={columnsAnchorEl}
          open={Boolean(columnsAnchorEl)}
          onClose={() => setColumnsAnchorEl(null)}
          PaperProps={{
            sx: {
              background: palette.background.paper,
              border: `1px solid ${palette.divider}`,
              borderRadius: "12px",
            },
          }}
        >
          <KDBox px={2} py={1.5} sx={{ borderBottom: `1px solid ${palette.divider}` }}>
            <KDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
              Select Columns
            </KDTypography>
          </KDBox>
          {columns.map((col) => (
            <MenuItem key={col.field} sx={{ py: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visibleFields.includes(col.field)}
                    onChange={() => handleToggleColumn(col.field)}
                    sx={{
                      color: palette.text.disabled,
                      "&.Mui-checked": { color: "info.main" },
                    }}
                  />
                }
                label={
                  <KDTypography variant="body2" color={darkMode ? "white" : "dark"} sx={{ opacity: 0.9 }}>
                    {col.header}
                  </KDTypography>
                }
              />
            </MenuItem>
          ))}
        </Menu>

        {/* ── Search field (below header, in card body) ── */}
        {searchable && (
          <KDBox mt={4} px={3} pb={2}>
            <TextField
              fullWidth
              placeholder={searchPlaceholder}
              variant="outlined"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: palette.text.disabled }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: palette.background.cardInner,
                  borderRadius: "12px",
                  color: palette.text.main,
                  "& fieldset": {
                    borderColor: palette.inputBorderColor,
                  },
                  "&:hover fieldset": {
                    borderColor: palette.inputBorderHoverColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: palette.text.disabled,
                  opacity: 1,
                },
              }}
            />
          </KDBox>
        )}

        {/* ── Table area ── */}
        <KDBox pt={2} px={3} pb={3}>
          {loading && (
            <KDBox display="flex" justifyContent="center" py={4}>
              <CircularProgress sx={{ color: "info.main" }} />
            </KDBox>
          )}
          {error && (
            <KDTypography color="error" mb={2}>
              {error}
            </KDTypography>
          )}
          {!loading && !error && (
            <KDTable
              columns={visibleColumns}
              rows={filteredRows}
              loading={false}
              emptyMessage={emptyMessage}
              onRowClick={onRowClick}
              defaultSortField={defaultSortField}
              defaultSortDirection={defaultSortDirection}
            />
          )}
        </KDBox>
      </Card>

      {/* ── Snackbar ── */}
      {snackbar && (
        <KDSnackbar
          open={snackbar.open}
          onClose={onSnackbarClose}
          severity={snackbar.severity || "info"}
          title={snackbar.title}
          message={snackbar.message}
        />
      )}
    </KDBox>
  );
}

ListPageTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  rows: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  emptyMessage: PropTypes.string,
  onRowClick: PropTypes.func,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  defaultSortField: PropTypes.string,
  defaultSortDirection: PropTypes.oneOf(["asc", "desc"]),
  /** Gradient color key from theme.palette.gradients (e.g. "info", "primary", "success") */
  headerColor: PropTypes.string,
  /** Array of field names visible by default. If omitted, all columns are shown. */
  defaultVisibleFields: PropTypes.arrayOf(PropTypes.string),
  /** Extra toolbar action nodes rendered in the header bar (before column icon) */
  extraToolbarActions: PropTypes.node,
  /** Snackbar notification state { open, severity, title, message } */
  snackbar: PropTypes.shape({
    open: PropTypes.bool,
    severity: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
  }),
  /** Callback to close snackbar */
  onSnackbarClose: PropTypes.func,
};

ListPageTemplate.defaultProps = {
  loading: false,
  error: "",
  emptyMessage: "No matching records found.",
  onRowClick: null,
  searchable: true,
  searchPlaceholder: "Search records...",
  defaultSortField: "",
  defaultSortDirection: "asc",
  headerColor: "info",
  defaultVisibleFields: null,
  extraToolbarActions: null,
  snackbar: null,
  onSnackbarClose: null,
};

export default ListPageTemplate;
